import { memo } from 'react';

import styled from 'styled-components';

import { createForm } from '@components/form';
import { useConfirmModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconDropRight } from '@components/icons/dropright';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRename } from '@components/icons/rename';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { hideModal, Modal } from '@components/modal';
import { showModal } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlow } from '@utils/transitions';
import { Transient } from '@utils/types';

import {
  useRemoveJourneyAction,
  useUpdateJourneyActionDescription,
} from 'api/journey-actions/hooks';
import {
  JourneyActionBreakingReferences,
  listBreakingReferencesByDeleteBranch,
} from 'api/journey-actions/requests';
import { BASE_ACTION_TYPE } from 'api/types';
import { useJourneyDetailParams } from 'ui/hooks';
import { useCollapseContext } from 'ui/journey-builder/hooks/use-collapsed-nodes';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import { nodeToActionBasic } from 'ui/journey-builder/node-types/common';
import {
  SourceHandle,
  StyledHandle,
  TargetHandle,
} from 'ui/journey-builder/node-types/node-container';
import { nodeCardAttributes } from 'ui/journey-builder/node-types/node-markers';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

import { BranchActionCard } from './branch-action-card';
import { DeleteBranchModal } from './delete-branch-modal';

import { ACTION_TYPES } from '../action-config';

const ACTION_TYPE = ACTION_TYPES.branch_base;

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  // @ts-ignore-next-line
  const isTravellerNode = Boolean(node.data.travellerJourneyId);

  return (
    <Container onContextMenu={(e) => e.preventDefault()}>
      <TargetHandle />
      <StyledNode $isTravellerNode={isTravellerNode} {...nodeCardAttributes}>
        <div style={{ position: 'relative', width: '100%' }}>
          <Stack orientation="horizontal" space={1} align="center" style={{ overflow: 'hidden' }}>
            <Text
              text={node.data.description}
              color="elementTeal200"
              font="semibold14"
              ellipsisWithTooltip
            />
            {!isTravellerNode && <Controls node={node} />}
          </Stack>
          {!isTravellerNode && <StackedCollapseCardIndicator isHidden={!node.data.isCollapsed} />}
        </div>
      </StyledNode>
      <SourceHandle style={{ bottom: -5 }} />
    </Container>
  );
});

type NodeProps = {
  node: JourneyNode<typeof ACTION_TYPE>;
};

const Controls = ({ node }: NodeProps) => {
  const { t } = useScopedTranslate(locale);
  const { collapseAction: collapseNode, expandAction: expandNode } = useCollapseContext();
  const removeMutation = useRemoveJourneyAction();
  const showBreakingRefsModal = useConfirmModal('LARGE');
  const { getValidNodes } = useNodes();

  const isCollapsed = Boolean(node.data?.isCollapsed);
  const toggleCollapsed = isCollapsed ? expandNode : collapseNode;

  const handleRemove = async () => {
    let dependants: JourneyActionBreakingReferences = [];
    try {
      dependants = await listBreakingReferencesByDeleteBranch(node.id);
    } catch (e) {
      showError(t('core.errors.generic'));
    }
    const hasBreakingReferences = dependants.length > 0;
    if ((node.data?.descendantCount ?? 0) > 0) {
      showModal({
        component: DeleteBranchModal,
        props: {
          node,
          onConfirm: () =>
            hasBreakingReferences
              ? handleShowBreakingRefs(dependants, node)
              : removeMutation.mutate(node.id),
        },
      });
    } else {
      removeMutation.mutate(node.id);
    }
  };

  const handleShowBreakingRefs = (
    dependants: JourneyActionBreakingReferences,
    node: NodeProps['node'],
  ) => {
    const nodes = getValidNodes();
    const confirmationNodes = dependants?.map(([parentNodeId, childNodeId]) => {
      const parentNode = nodes.find((node) => node.id === parentNodeId);
      const childNode = nodes.find((node) => node.id === childNodeId);
      return [parentNode, childNode];
    }) as [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][];
    setTimeout(() =>
      showBreakingRefsModal({
        title: t('warning'),
        headerIcon: <IconFormIndicatorWarning />,
        description: t('breakConnectionsText'),
        buttonText: t('deleteBranch'),
        buttonIcon: <IconTrash />,
        buttonVariant: 'primary-red',
        onConfirm: () => removeMutation.mutate(node.id),
        children: (
          <Stack space={4}>
            <Text font="semibold14">{t('confirmActionLabel')}</Text>
            {confirmationNodes.map(([parentNode, childNode]) => {
              return (
                <Stack
                  key={`${parentNode.id}-${childNode.id}`}
                  orientation="horizontal"
                  justify="space-between"
                  align="center"
                  space={2}
                >
                  <BranchActionCard action={nodeToActionBasic(childNode)} isDeleteAction />
                  <IconMappingOff color="elementRed100" />
                  <BranchActionCard action={nodeToActionBasic(parentNode)} />
                </Stack>
              );
            })}
          </Stack>
        ),
      }),
    );
  };

  return (
    <ControlsContainer>
      <Menu
        trigger={IconButton}
        triggerProps={{
          size: 'sm',
          variant: 'ghost-purple',
          icon: <IconMoreVertical size={16} color="teal200" />,
        }}
      >
        <Menu.Item
          label={t('rename')}
          icon={<IconRename />}
          onSelect={() => showModal({ component: RenameModal, props: { node } })}
        />
        <Menu.Item
          label={t('core.delete')}
          variant="danger"
          onSelect={handleRemove}
          icon={<IconTrash />}
        />
      </Menu>
      <IconButton
        size="sm"
        variant="ghost-purple"
        stopPropagation
        preventDefault
        isRotated={!isCollapsed}
        degrees={90}
        icon={<IconDropRight color="teal200" size={16} />}
        onClick={() => toggleCollapsed(node.id)}
      />
    </ControlsContainer>
  );
};

type RenameData = { description: string };

const Form = createForm<RenameData>();

const RenameModal = ({ node }: NodeProps) => {
  const { t } = useScopedTranslate(locale);
  const { journeyId } = useJourneyDetailParams();
  const editMutation = useUpdateJourneyActionDescription(node.id, journeyId);

  const handleSubmit = async ({ description }: RenameData) => {
    await editMutation.mutateAsync(description);
    requestAnimationFrame(() => {
      hideModal();
    });
  };

  return (
    <Modal>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <Form.TextField
            id="description"
            label={t('rename')}
            hideLabel
            defaultValue={node.data.description}
            validators={['required']}
          />
          <Form.Actions>
            <Form.Reset
              text={t('core.cancel')}
              isDisabled={editMutation.isLoading}
              variant="secondary"
              onClick={hideModal}
            />
            <Form.Submit
              isDisabled={editMutation.isLoading}
              variant="primary-blue"
              dataTestid="rename-branch-base"
              text={t('rename')}
            />
          </Form.Actions>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const ControlsContainer = styled.div({
  display: 'flex',

  button: {
    height: pxToRem(24),
    width: pxToRem(24),
  },
});

const Container = styled.div({
  width: pxToRem(320),
  display: 'flex',
  justifyContent: 'center',
  cursor: 'default',
  [StyledHandle]: {
    opacity: 0,
  },
});

const SHARED_STYLE = {
  backgroundColor: theme.color.elementTeal10,
  borderRadius: pxToRem(4),
  borderWidth: pxToRem(1),
  borderColor: theme.color.teal100,
  borderStyle: 'solid',
};

const StyledNode = styled.div<{ $isTravellerNode: boolean }>(({ $isTravellerNode }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: '100%',
  height: pxToRem(40),
  padding: $isTravellerNode
    ? pxToRem(8)
    : `${pxToRem(8)} ${pxToRem(8)} ${pxToRem(8)} ${pxToRem(12)}`,

  // To mitigate gaps left by the transparent handles
  marginTop: -5,
  marginBottom: -5,

  ...SHARED_STYLE,
}));

type StackedCollapseCardIndicatorProps = {
  isHidden: boolean;
};

const StackedCollapseCardIndicator = ({ isHidden }: StackedCollapseCardIndicatorProps) => {
  return (
    <AnimatedContainer className="stacked-collapse-cards" $isHidden={isHidden}>
      <CollapsedCardsIndicator
        style={{ top: '2px', left: pxToRem(-4), width: `calc(100% - ${pxToRem(16)})` }}
      />
      <CollapsedCardsIndicator style={{ left: pxToRem(-8), width: `calc(100% - ${pxToRem(8)})` }} />
    </AnimatedContainer>
  );
};

const AnimatedContainer = styled.div<Transient<StackedCollapseCardIndicatorProps>>(
  ({ $isHidden }) => ({
    position: 'absolute',
    width: `calc(100% + ${pxToRem(20)})`,
    height: pxToRem(8),
    top: $isHidden ? pxToRem(20) : pxToRem(24),
    transition: transitionSlow('top'),
  }),
);

const CollapsedCardsIndicator = styled.div({
  position: 'absolute',
  height: '120%',
  boxSizing: 'border-box',
  zIndex: -1,
  ...SHARED_STYLE,
});

const locale = {
  en: {
    rename: 'Rename',
    deleteBranch: 'Delete branch',
    confirmActionLabel: 'Connected actions',
    warning: 'Warning',
    breakConnectionsText:
      'Deleting this branch will break connected values between the actions in other branches. Are you sure you want to delete this branch?',
  },
};
